import { ReactNode } from "react";

import { Icon } from "../IconControls/IconControls";

import style from "./TriStateCheckboxControl.module.scss";

interface TriStateCheckboxProps {
    checkedStates: boolean[];
    onClick?: (action: OnClickAction) => void;
    className?: string;
    actionOnIndeterminateClick?: OnClickAction;
    dataCy?: string;
    disabled?: boolean;
    id?: string;
    label?: ReactNode;
}

type IconType = "minus-square-fill" | "check-square-fill" | "square-regular";

enum OnClickAction {
    SelectAll,
    DeselectAll
}

function useGetCheckboxState(checkedStates: boolean[], actionOnIndeterminateClick?: OnClickAction): { iconType: IconType, nextAction: OnClickAction } {
    let nextAction: OnClickAction = OnClickAction.SelectAll;

    let iconType: IconType = "minus-square-fill";

    if (checkedStates.every(s => !!s)) {
        iconType = "check-square-fill";
        nextAction = OnClickAction.DeselectAll;
    }
    else if (checkedStates.every(s => !s)) {
        iconType = "square-regular";
        nextAction = OnClickAction.SelectAll;
    }
    else if (actionOnIndeterminateClick) {
        iconType = "square-regular";
        nextAction = actionOnIndeterminateClick;
    }

    return { iconType, nextAction };
}

function TriStateCheckboxControl(props: TriStateCheckboxProps): JSX.Element {
    const { iconType, nextAction } = useGetCheckboxState(props.checkedStates, props.actionOnIndeterminateClick);

    const onClick = (): void => !props.disabled && props.onClick(nextAction);

    return (
        <>
            <div
                id={props.id}
                data-cy={props.dataCy}
                onClick={onClick}
                className={`${style.triStateCheckbox} ${props.disabled ? style.disabled : ""} ${props.className ?? ""}`.trim()}
                style={style}
            >
                <Icon type={iconType} />
            </div>
            {props.label && (
                <span
                    onClick={onClick}
                    className={`${style.triStateLabel} ${props.disabled ? style.disabled : ""}`}
                >
                    {props.label}
                </span>
            )}
        </>
    );
}

export { OnClickAction, TriStateCheckboxControl, TriStateCheckboxProps, useGetCheckboxState };
